/**
 * SSR Data Context
 *
 * Allows the server to pre-fetch page data (case studies, privacy, terms, etc.)
 * and pass it to React components during SSR, avoiding client-side fetches
 * and ensuring full content is present in the initial HTML for SEO.
 *
 * On the client, the data is hydrated from window.__SSR_DATA__ so that
 * React hydration matches the server-rendered output.
 */

import { createContext, useContext } from 'react';

/** Shape of a CMS page as returned by the API / DB. */
export interface SSRPageData {
  id: number;
  slug: string;
  language: string;
  title: string;
  content?: string;
  contentHtml?: string;
  metaDescription?: string;
  layout?: string;
}

/** Shape of a listing page item (case studies / resources). */
export interface SSRListingPage {
  id: number;
  slug: string;
  pageType: string;
  layout: string;
  previewColor?: string | null;
  sortOrder: number;
  title: string;
  content?: string;
  metaDescription?: string | null;
  previewContent?: string | null;
  previewContentHtml?: string | null;
  showInListing?: boolean;
  showInMenu?: boolean;
}

/** Data bag passed from the server into the React tree. */
export interface SSRData {
  pageData?: SSRPageData;
  listingPages?: SSRListingPage[];
}

/**
 * Context that holds server-fetched data.
 * Empty object by default (no pre-fetched data available).
 */
export const SSRDataContext = createContext<SSRData>({});

/**
 * One-time consumption flags.
 * SSR data is only valid for the very first component mount (hydration).
 * After that, components must fetch fresh data from the API so that
 * client-side navigations between /case-studies and /resources never
 * see each other's stale SSR payload.
 */
let _pageDataConsumed = false;
let _listingPagesConsumed = false;

/**
 * Reset consumption flags so the next SSR render gets fresh data.
 * Must be called at the start of every server-side render pass;
 * without this, the module-level flags persist across requests
 * because the SSR bundle is cached in memory.
 */
export function resetSSRConsumptionFlags(): void {
  _pageDataConsumed = false;
  _listingPagesConsumed = false;
}

/**
 * Hook to retrieve pre-fetched page data from the SSR context.
 * Returns the data only once (during initial hydration); subsequent
 * calls return undefined so that components fetch from the API.
 */
export function useSSRPageData(): SSRPageData | undefined {
  const ctx = useContext(SSRDataContext);
  if (_pageDataConsumed) return undefined;
  _pageDataConsumed = true;
  return ctx.pageData;
}

/**
 * Hook to retrieve pre-fetched listing pages from the SSR context.
 * Returns the data only once (during initial hydration); subsequent
 * calls return undefined so that components fetch from the API.
 */
export function useSSRListingPages(): SSRListingPage[] | undefined {
  const ctx = useContext(SSRDataContext);
  if (_listingPagesConsumed) return undefined;
  _listingPagesConsumed = true;
  return ctx.listingPages;
}

/**
 * Declare the global window property for TypeScript.
 * The server injects this as a <script> tag for client hydration.
 */
declare global {
  interface Window {
    __SSR_DATA__?: SSRData;
  }
}
